'use client';

import { faCheck, faClock } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { ReactNode } from 'react';

import { CardAppointmentHeaderProps } from './_primitives/card-appointment-header.component';
import { CardAppointmentRealized } from './_primitives/card-appointment-realized.component';
import { LabeledField } from './_primitives/labeled-field.component';
import { getPartnerProfilePath, getProfessionalProfilePath } from './_utils/appointment-entity-path';
import { AppointmentCardExpanded } from './card-appointment-expanded.component';
import { CardAppointmentLabExpanded } from './card-appointment-lab-expanded.component';
import { AppointmentUnconfirmedSupport } from './variants/_components/appointment-items/appointment-unconfirmed-support.component';
import { AppointmentLocation, getAppointmentLocation } from './variants/_services/get-appointment-location.service';

import { ScheduleType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { Pill } from '@/components/ui/pill/pill.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { useAppointmentReviewStatus } from '@/hooks/fetch/appointment/use-appointment-review-status.hook';
import { Appointment } from '@/types/appointments.types';
import { AppointmentMode, AppointmentStatus, AppointmentType } from '@/types/enums/appointments.enum';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { dateFormatter } from '@/utils/formatters/date';
import { formatClientName } from '@/utils/formatters/format-to-title-case';
import { partnerDisplayName, resolveAppointmentEntity } from '@/utils/formatters/appointment-entity';

interface CardAppointmentRealizedByTypeProps {
  appointment: Appointment;
  defaultOpen?: boolean;
}

interface RealizedVariantConfig {
  testId: string;
  entity: CardAppointmentHeaderProps;
  procedureName?: string;
  formattedDate?: string;
  dateLabel?: string;
  location?: AppointmentLocation & { phonePrefix?: string };
  preAccordionExtras?: ReactNode;
  expandedContent: ReactNode;
}

function buildVariantConfig(appointment: Appointment): RealizedVariantConfig {
  const entity = resolveAppointmentEntity(appointment);
  const professionalName = entity?.isProfessional ? entity.name : '';
  const professionalEntity: CardAppointmentHeaderProps = {
    avatarSrc: appointment.professional?.avatar,
    avatarAlt: professionalName,
    avatarType: EntityTypeEnum.PROFESSIONAL,
    name: professionalName,
    href: getProfessionalProfilePath(appointment.professional),
  };
  const partnerName = partnerDisplayName(appointment.partner);
  const partnerEntity: CardAppointmentHeaderProps = {
    avatarSrc: appointment.partner.avatar,
    avatarAlt: partnerName,
    avatarType: EntityTypeEnum.LABORATORY_COLLECT_POINT,
    name: partnerName,
    href: getPartnerProfilePath(appointment),
  };
  const procedureName = appointment.items.map((item) => item.procedure.name).join(', ');
  const partnerLocation = getAppointmentLocation(appointment);
  const defaultExpandedContent = (
    <AppointmentCardExpanded appointment={appointment} noLeadingHr showCancelButton={false} />
  );

  if (appointment.mode === AppointmentMode.STANDALONE) {
    return {
      testId: DataTestIdEnum.APPOINTMENT_CARD_STANDALONE,
      entity: appointment.professional ? professionalEntity : partnerEntity,
      procedureName,
      formattedDate: appointment.checkedAt
        ? `${dateFormatter(appointment.checkedAt, 'DD/MM/YYYY [às] HH:mm')} (horário de Brasília)`
        : undefined,
      dateLabel: 'Data e hora do atendimento',
      location: partnerLocation,
      expandedContent: defaultExpandedContent,
    };
  }

  if (appointment.type === AppointmentType.LABORATORIAL) {
    const startDate = dateFormatter(appointment.date, 'date');
    const startHour = dateFormatter(appointment.date, 'HH:mm');
    const endHour = appointment.end ? dateFormatter(appointment.end, 'HH:mm') : null;
    const formattedDate = endHour
      ? `${startDate}, das ${startHour} às ${endHour} (horário de Brasília), por ordem de chegada`
      : `${startDate} às ${startHour} (horário de Brasília), por ordem de chegada`;
    const examCount = appointment.items.length;
    const examSummary = `${examCount} ${examCount === 1 ? 'exame' : 'exames'} (Consulte a lista abaixo)`;

    return {
      testId: DataTestIdEnum.APPOINTMENT_CARD_LAB,
      entity: partnerEntity,
      formattedDate,
      location: partnerLocation,
      preAccordionExtras: (
        <LabeledField label="Exames">
          <Typography>{examSummary}</Typography>
        </LabeledField>
      ),
      expandedContent: (
        <CardAppointmentLabExpanded appointment={appointment} noLeadingHr showCancelButton={false} />
      ),
    };
  }

  const standardFormattedDate = appointment.date
    ? `${dateFormatter(appointment.date, 'DD/MM/YYYY [às] HH:mm')} (horário de Brasília)`
    : undefined;

  if (appointment.isTelehealth) {
    return {
      testId: DataTestIdEnum.APPOINTMENT_CARD_TELEMED,
      entity: professionalEntity,
      procedureName,
      formattedDate: standardFormattedDate,
      expandedContent: defaultExpandedContent,
    };
  }

  if (appointment.scheduleType === ScheduleType.ARRIVAL_ORDER) {
    const day = dateFormatter(appointment.date, 'date');
    const hour = dateFormatter(appointment.date, 'HH:mm');
    const endHour = appointment.end ? dateFormatter(appointment.end, 'HH:mm') : null;
    const formattedDate = endHour
      ? `${day} das ${hour} às ${endHour} (Horário de Brasília), por ordem de chegada`
      : `${day} às ${hour} (Horário de Brasília)`;

    return {
      testId: DataTestIdEnum.APPOINTMENT_CARD_ARRIVAL_ORDER,
      entity: professionalEntity,
      procedureName,
      formattedDate,
      location: partnerLocation,
      expandedContent: defaultExpandedContent,
    };
  }

  if (appointment.scheduleType === ScheduleType.FLEX) {
    return {
      testId: DataTestIdEnum.APPOINTMENT_CARD_FLEX,
      entity: professionalEntity,
      procedureName,
      formattedDate: standardFormattedDate,
      location: partnerLocation,
      expandedContent: defaultExpandedContent,
    };
  }

  return {
    testId: DataTestIdEnum.APPOINTMENT_CARD_SCHEDULED,
    entity: professionalEntity,
    procedureName,
    formattedDate: standardFormattedDate,
    location: partnerLocation,
    expandedContent: defaultExpandedContent,
  };
}

export function CardAppointmentRealizedByType({ appointment, defaultOpen }: CardAppointmentRealizedByTypeProps) {
  const reviewStatus = useAppointmentReviewStatus(appointment);
  const patientName = formatClientName(appointment.patient);
  const config = buildVariantConfig(appointment);
  const isCheckedIn = appointment.status === AppointmentStatus.FINISHED;

  return (
    <CardAppointmentRealized
      testId={config.testId}
      statusPill={isCheckedIn
        ? (
            <Pill
              variant="success"
              icon={faCheck}
              label="Realizado"
              className="font-normal"
              data-testid={DataTestIdEnum.APPOINTMENT_CARD_STATUS_CONFIRMED}
            />
          )
        : (
            <Pill
              variant="warning"
              icon={faClock}
              label="Atendimento não confirmado"
              className="font-normal"
              data-testid={DataTestIdEnum.APPOINTMENT_CARD_STATUS_UNCONFIRMED}
            />
          )}
      unconfirmedSupport={
        isCheckedIn ? undefined : <AppointmentUnconfirmedSupport appointmentType={appointment.type} />
      }
      defaultOpen={defaultOpen}
      entity={config.entity}
      procedureName={config.procedureName}
      formattedDate={config.formattedDate}
      dateLabel={config.dateLabel}
      patientName={patientName}
      appointment={appointment}
      location={config.location}
      preAccordionExtras={config.preAccordionExtras}
      expandedContent={config.expandedContent}
      reviewStatus={reviewStatus}
    />
  );
}
